/**
 * Client entry point.
 *
 * TanStack Start resolves `src/client.tsx` by convention and makes it the input of
 * the client build. It replaces the previous `src/main.tsx`, which mounted with
 * `createRoot` into `<div id="root">`. That no longer works: pages are prerendered to
 * real HTML at build time, and `main.tsx` was guarded by `if (!rootElement.innerHTML)`
 * -- against prerendered markup that guard is false, so React would never mount and
 * the page would be inert (no add-to-cart, no client navigation).
 *
 * Hydration targets `document` because the root route's `shellComponent` renders
 * <html>/<head>/<body> itself.
 */

import { StrictMode, startTransition } from "react";
import { hydrateRoot } from "react-dom/client";
import { StartClient } from "@tanstack/react-start/client";

// ── Hide platform watermark badges injected by hosting services ──
const hidePlatformBadge = () => {
  const selectors = [
    "#lovable-badge",
    "#gpt-engineer-badge",
    "[data-lovable-badge]",
    "[data-gpt-engineer-badge]",
    '[class*="lovable-badge"]',
    '[id*="lovable"]',
    'iframe[src*="lovable.dev"]',
    'iframe[src*="gptengineer"]',
  ];
  selectors.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => {
      (el as HTMLElement).style.setProperty("display", "none", "important");
    });
  });
};

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <StartClient />
    </StrictMode>,
  );

  // Run only after hydration is scheduled. These badges are injected by the host
  // platform outside React's tree, so hiding them cannot run before hydration without
  // mutating markup React is about to reconcile.
  hidePlatformBadge();
  const badgeObserver = new MutationObserver(hidePlatformBadge);
  badgeObserver.observe(document.documentElement, { childList: true, subtree: true });
});
